import React, { useState, useRef } from 'react';
import EnrollForm from '../EnrollForm/EnrollForm';
import './RoadmapSection.css';

const PHASES = [
  {
    label: 'PHASE 1 · MONTHS 1–4',
    title: 'Electrical Basics & Sensors',
    icon: '🔌',
    sub: 'Circuits · Arduino · Sensors',
    tags: ['4 months', '32 live sessions', '90 min/session', '2 per week', 'Arduino Kit'],
    desc: 'Students start from zero — no prior knowledge needed. Through hands-on experiments students learn electricity, program their first microcontroller, and present a working multi-sensor project by Week 16.',
    weeks: [
      { week: 'W1–2',   learn: "Electricity, voltage, current, resistance; Ohm's Law; safety",           build: '5-LED circuit; understand current flow' },
      { week: 'W3–4',   learn: 'Electronic components; reading circuit diagrams; breadboard',              build: 'Decode & assemble a schematic independently' },
      { week: 'W5–6',   learn: 'Digital vs analog signals; multimeter use; signal tracing',               build: 'Measure real voltages in a live circuit' },
      { week: 'W7–8',   learn: 'Arduino Uno; pins, IDE setup, Blink sketch',                              build: 'Upload first program; LED blink patterns' },
      { week: 'W9–10',  learn: 'Temperature (LM35) & Light sensor (LDR) with Arduino',                    build: 'Temperature & brightness data logger' },
      { week: 'W11–12', learn: 'Ultrasonic (HC-SR04) and IR obstacle sensor',                             build: 'Automatic lamp + proximity alarm' },
      { week: 'W13–14', learn: 'Sound sensor and Humidity (DHT11); threshold triggers',                   build: 'Smart multi-condition alert system' },
      { week: 'W15–16', learn: 'Phase 1 Final Project: Multi-sensor dashboard',                           build: 'Live demo — ibots CircuitStar Certificate', bold: true },
    ],
    cert: {
      title: 'ibots CircuitStar Certificate — Phase 1',
      quote: '"I can build circuits and read data from the real world"',
      skills: ['Electrical Circuits', 'Arduino', 'Sensor Interfacing', 'Data Logging'],
    },
    nextPhase: 'Next: Embedded Systems & IoT — Months 5–8 · ESP32, cloud dashboards, MQTT, smart home devices',
  },
  {
    label: 'PHASE 2 · MONTHS 5–8',
    title: 'Embedded Systems & IoT',
    icon: '📊',
    sub: 'ESP32 · MQTT · Cloud',
    tags: ['4 months', '32 live sessions', '90 min/session', 'ESP32 + Cloud', 'ESP8266 Kit'],
    desc: "Students build devices that connect to the internet — the foundation of Smart Homes and Industry 4.0. By Month 8 every student has a live IoT device controllable from a smartphone. Most engineering graduates haven't built this. Your child will at 13.",
    weeks: [
      { week: 'W1–2',   learn: 'ESP8266/ESP32 Wi-Fi board; Arduino IDE for IoT',              build: 'Connect microcontroller to Wi-Fi; ping test' },
      { week: 'W3–4',   learn: 'Programming logic: conditionals, loops, functions',            build: 'Smart toggle switch with debounced logic' },
      { week: 'W5–6',   learn: 'UART, I2C, SPI protocols; multi-sensor bus',                  build: 'Multiple sensors on shared I2C bus' },
      { week: 'W7–8',   learn: 'ThingSpeak cloud: upload sensor data; live charts',            build: 'Personal sensor dashboard on the internet' },
      { week: 'W9–10',  learn: 'MQTT; Blynk IoT app; LED and relay from smartphone',          build: 'Control a device remotely from mobile' },
      { week: 'W11–12', learn: 'Servo motor; relay; PWM control; feedback loops',             build: 'IoT-controlled door lock system' },
      { week: 'W13–14', learn: 'Edge computing: local rules without cloud',                    build: 'Offline smart room controller' },
      { week: 'W15–16', learn: 'Phase 2 Final Project: Complete IoT device',                  build: 'Full demo + video — ibots IoT Builder Certificate', bold: true },
    ],
    cert: {
      title: 'ibots IoT Builder Certificate — Phase 2',
      quote: '"I can connect devices to the internet and control them from anywhere"',
      skills: ['ESP32 Wi-Fi', 'Cloud Connectivity', 'MQTT / Blynk', 'Actuator Control'],
    },
    nextPhase: 'Next: Robotics & Drones — Months 9–12 · Robot building, drone programming, Demo Day',
  },
  {
    label: 'PHASE 3 · MONTHS 9–12',
    title: 'Robotics & Drones',
    icon: '🤖',
    sub: 'Robot · Drone · Demo Day',
    tags: ['4 months', '32 live sessions', 'Robot Build', 'Drone Fly', 'Demo Day'],
    desc: 'The capstone phase every student gets excited about. Assemble a robot, program autonomous navigation, build a drone, calibrate the flight controller, and fly it. Demo Day is a parent-invited event — come watch your child fly what they built with their own hands.',
    weeks: [
      { week: 'W1–2',   learn: 'DC motors, gear ratio, L298N H-bridge driver',                          build: 'Build & drive a 2-wheel robot chassis' },
      { week: 'W3–4',   learn: 'IR array for line following; calibration',                              build: 'Line-following robot on a track' },
      { week: 'W5–6',   learn: 'Ultrasonic sweep; obstacle-detection logic',                            build: 'Robot that navigates around obstacles' },
      { week: 'W7–8',   learn: 'Bluetooth HC-05; custom Android app via MIT App Inventor',              build: 'Control robot from your own phone app' },
      { week: 'W9–10',  learn: 'Quadcopter physics; thrust/torque; DGCA safety rules',                  build: 'Pass theory + safety certification quiz' },
      { week: 'W11–12', learn: 'Drone frame assembly; brushless motors; ESC; FC setup',                 build: 'Fully assembled and calibrated drone' },
      { week: 'W13–14', learn: 'Betaflight configuration; PID tuning; tethered hover',                  build: 'First stable hover — fly what you built!' },
      { week: 'W15–16', learn: 'Capstone Demo Day: robot + drone + presentation',                       build: 'Parent-invite Demo Day — ibots STEM Maker Certificate', bold: true },
    ],
    cert: {
      title: 'ibots STEM Maker Certificate + Young Innovator Award — Phase 3',
      quote: '"I can build robots that think and drones that fly — from scratch"',
      skills: ['Autonomous Robotics', 'Bluetooth Control', 'Drone Assembly', 'Live Demo'],
    },
    nextPhase: null,
  },
];

function RoadmapSection({ onClose }) {
  const [activePhase, setActivePhase] = useState(0);
  const [showEnroll, setShowEnroll] = useState(false);
  const phaseDetailRef = useRef(null);
  const phase = PHASES[activePhase];

  const handlePhaseChange = (index) => {
    setActivePhase(index);
    setTimeout(() => {
      phaseDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="roadmap">
      <div className="roadmap__inner">

        {/* Close */}
        <button className="roadmap__close" onClick={onClose}>✕ Close Roadmap</button>

        {/* Header */}
        <div className="roadmap__header">
          <div className="roadmap__header-left">
            <p className="roadmap__tag">🌐 ONLINE · GRADE 6–9 · LIVE ZOOM / GOOGLE MEET</p>
            <h2 className="roadmap__title">Innovators Programme — 12-Month Roadmap</h2>
            <p className="roadmap__subtitle">
              "From Curiosity to Creator — Learn from Anywhere." Kits couriered to your home.
              Real hardware. Real projects.
            </p>
          </div>
          <div className="roadmap__stats">
            {[
              { num: '12',  label: 'Months' },
              { num: '96',  label: 'Sessions' },
              { num: '10+', label: 'Projects' },
              { num: '3',   label: 'Certificates' },
            ].map((s, i) => (
              <div key={i} className="roadmap__stat">
                <span className="roadmap__stat-num">{s.num}</span>
                <span className="roadmap__stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Phase Tabs */}
        <div className="roadmap__phase-tabs">
          {PHASES.map((p, i) => (
            <div
              key={i}
              className={`roadmap__phase-tab ${activePhase === i ? 'roadmap__phase-tab--active' : ''}`}
              onClick={() => handlePhaseChange(i)}
            >
              <p className="roadmap__phase-label">{p.label}</p>
              <p className="roadmap__phase-title">{p.icon} {p.title}</p>
              <p className="roadmap__phase-sub">{p.sub}</p>
            </div>
          ))}
        </div>

        {/* Phase Detail */}
        <div ref={phaseDetailRef}>

          <div className="roadmap__phase-detail">
            <div className="roadmap__phase-left">
              <h3 className="roadmap__phase-heading">
                {phase.icon} Phase {activePhase + 1} — {phase.title}
              </h3>
              <p className="roadmap__phase-desc">{phase.desc}</p>
              <div className="roadmap__phase-tags">
                {phase.tags.map((t, i) => (
                  <span key={i} className="roadmap__phase-tag">{t}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Weekly Table */}
          <div className="roadmap__table-wrap" style={{ marginTop: 24 }}>
            <table className="roadmap__table">
              <thead>
                <tr>
                  <th>Week</th>
                  <th>What Students Learn</th>
                  <th>What They Build</th>
                </tr>
              </thead>
              <tbody>
                {phase.weeks.map((w, i) => (
                  <tr key={i}>
                    <td className="roadmap__week">{w.week}</td>
                    <td className={w.bold ? 'roadmap__bold' : ''}>{w.learn}</td>
                    <td className={w.bold ? 'roadmap__bold' : ''}>{w.build}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Certificate */}
          <div className="roadmap__cert" style={{ marginTop: 24 }}>
            <span className="roadmap__cert-icon">🏅</span>
            <div>
              <h4 className="roadmap__cert-title">{phase.cert.title}</h4>
              <p className="roadmap__cert-quote">{phase.cert.quote}</p>
              <div className="roadmap__cert-skills">
                {phase.cert.skills.map((s, i) => (
                  <span key={i} className="roadmap__cert-skill">{s}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Phase 3 only */}
          {activePhase === 2 && (
            <>
              <div className="roadmap__trophy-note" style={{ marginTop: 24 }}>
                🏆 Students completing all 3 phases receive a framed certificate + trophy
                at the Annual ibots STEM Showcase — a public, parent-invited event in Coimbatore.
              </div>

              <div className="roadmap__enroll-banner" style={{ marginTop: 16 }}>
                <div>
                  <p className="roadmap__enroll-title">Ready to Start the Full 12-Month Journey?</p>
                  <p className="roadmap__enroll-sub">
                    Pay per phase · No lock-in · Kits delivered free · 10% sibling discount
                  </p>
                </div>
                <button className="roadmap__enroll-btn" onClick={() => setShowEnroll(true)}>
                  Enroll in Online Programme
                </button>
              </div>
            </>
          )}

          {/* Continue to Next Phase */}
          {phase.nextPhase && (
            <div className="roadmap__continue" style={{ marginTop: 24 }}>
              <div>
                <p className="roadmap__continue-title">
                  🎉 Phase {activePhase + 1} Complete! Ready for Phase {activePhase + 2}?
                </p>
                <p className="roadmap__continue-sub">{phase.nextPhase}</p>
              </div>
              <button
                className="roadmap__continue-btn"
                onClick={() => handlePhaseChange(activePhase + 1)}
              >
                Continue to Phase {activePhase + 2} →
              </button>
            </div>
          )}

        </div>

        {/* Enroll Form Popup */}
        {showEnroll && <EnrollForm onClose={() => setShowEnroll(false)} />}

      </div>
    </div>
  );
}

export default RoadmapSection;
